import { inject, Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { BehaviorSubject, filter, Observable, switchMap, take } from 'rxjs';
import { UserRole } from '@/types/user.types';
import { ProfileService } from '@/pages/profile/profile.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  keycloak = inject(Keycloak);
  profileService = inject(ProfileService);

  private rolesSubject = new BehaviorSubject<string[]>([]);
  roles$ = this.rolesSubject.asObservable();

  private readySubject = new BehaviorSubject(false);
  isReady$ = this.readySubject.asObservable();

  constructor() {
    if (this.keycloak.authenticated) {
      this.loadRoles();
      this.readySubject.next(true);
    } else {
      this.keycloak.onAuthSuccess = () => {
        this.loadRoles();
        this.readySubject.next(true);
      };
      this.keycloak.onAuthLogout = () => {
        this.rolesSubject.next([]);
        this.readySubject.next(false);
      };
    }
  }

  init() {
    this.loadRoles();
  }

  private loadRoles() {
    const tokenParsed = this.keycloak.tokenParsed;

    if (!tokenParsed) {
      this.rolesSubject.next([]);
      return;
    }

    // 1️⃣ Rôles du client front
    const frontClientRoles = tokenParsed.resource_access?.[environment.keycloak.clientId]?.roles || [];

    // 2️⃣ Rôles du client commun
    const commonClientRoles = tokenParsed.resource_access?.[environment.keycloak.commonClientId]?.roles || [];

    // 3️⃣ Fusionner les rôles sans doublons
    const allRoles = Array.from(new Set([...frontClientRoles, ...commonClientRoles]));

    // 4️⃣ Mettre à jour le BehaviorSubject
    this.rolesSubject.next(allRoles);
  }

  isAuthenticated(): boolean {
    return this.keycloak.authenticated ?? false;
  }

  getObservableRoles(): Observable<string[]> {
    return this.isReady$.pipe(
      filter(ready => ready),
      switchMap(() => this.roles$),
      take(1)
    );
  }

  getUserId(): Promise<string | undefined> {
    return this.isAuthenticated()
      ? this.profileService
          .getProfile()
          .toPromise()
          .then(user => user?.id)
      : Promise.resolve(undefined);
  }

  login(): void {
    this.keycloak.login();
  }

  logout(): void {
    this.keycloak.logout();
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  hasRoles(roles: string[]): boolean {
    return roles.some(role => this.getRoles().includes(role));
  }

  // Helpers optionnels
  isRedacteur(): boolean {
    return this.getRoles().includes(UserRole.REDACTEUR_AC);
  }

  isPublisher(): boolean {
    return this.getRoles().includes(UserRole.PUB_AC);
  }

  isValidator(): boolean {
    return this.getRoles().includes(UserRole.VALIDATEUR_AC);
  }

  isCncmp(): boolean {
    return this.getRoles().includes(UserRole.CNCMP);
  }

  isCpmp(): boolean {
    return this.getRoles().includes(UserRole.CPMP);
  }

  getRoles(): string[] {
    return this.rolesSubject.getValue() ?? [];
  }
}
